/**
 * The Spaces page's pure selectors: each space with how many of the
 * organization's chats it holds and when one of them last moved, and the
 * words its row says about that. The caller passes chats already scoped to
 * the organization (`selectOrgThreads`); nothing here widens that scope.
 *
 * @module components/spaces/spacesPageLogic
 */
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";

import type { Space } from "../../state/spaces";
import { compactAge, threadActivityMs } from "../home/homeLogic";

export interface SpaceSummary {
  readonly space: Space;
  readonly chats: number;
  /** The latest activity in any of its chats; null when it holds none. */
  readonly lastActivityMs: number | null;
}

/**
 * One summary per space, in the order the server lists them (oldest first,
 * as the sidebar shows them). A chat assigned to a space not in the list is
 * ignored.
 */
export function summarizeSpaces(
  spaces: ReadonlyArray<Space>,
  threads: ReadonlyArray<Pick<EnvironmentThreadShell, "id" | "updatedAt" | "latestUserMessageAt">>,
  spaceIdByThreadId: ReadonlyMap<string, string>,
): SpaceSummary[] {
  const totals = new Map<string, { chats: number; lastActivityMs: number | null }>();
  for (const space of spaces) totals.set(space.id, { chats: 0, lastActivityMs: null });
  for (const thread of threads) {
    const spaceId = spaceIdByThreadId.get(thread.id);
    const total = spaceId === undefined ? undefined : totals.get(spaceId);
    if (total === undefined) continue;
    total.chats += 1;
    const activity = threadActivityMs(thread);
    // An unreadable timestamp reads as the epoch: it counts the chat, not a time.
    if (activity > 0 && (total.lastActivityMs === null || activity > total.lastActivityMs)) {
      total.lastActivityMs = activity;
    }
  }
  return spaces.map((space) => ({ space, ...totals.get(space.id)! }));
}

/** "No chats yet", "1 chat", "12 chats". */
export function spaceChatsLabel(chats: number): string {
  if (chats === 0) return "No chats yet";
  return `${chats} ${chats === 1 ? "chat" : "chats"}`;
}

/** "Active now", "Active 3h ago"; null when nothing has happened in the space. */
export function spaceActivityLabel(lastActivityMs: number | null, now: number): string | null {
  if (lastActivityMs === null) return null;
  const age = compactAge(new Date(lastActivityMs).toISOString(), now);
  return age === "now" ? "Active now" : `Active ${age} ago`;
}
